<template>
  <div
    class="lense-variant-selector relative"
    data-allow-mismatch
    data-testid="lense-variant-selector"
  >
    <div v-if="isLoading" class="grid grid-cols-2 gap-4">
      <Skeleton is-full-width class="h-8" />
      <Skeleton is-full-width class="h-8" />
      <Skeleton is-full-width class="h-32" />
      <Skeleton is-full-width class="h-32" />
    </div>
    <div v-else-if="!hasError">
      <div class="absolute right-0 top-10 z-10">
        <Tooltip
          v-if="informationPopover && informationPopover.length"
          placement="bottom-end"
          :flip="false"
        >
          <span class="flex cursor-pointer items-center">
            <LazyIconFielmannInfoCircle class="mr-2 size-3" />
            {{ $t('pdp.information') }}</span
          >
          <template #popper>
            <div class="text-sm">
              <div v-for="(item, i) in informationPopover" :key="i">
                <h4 class="font-medium">{{ item.title }}</h4>
                <p class="mb-1">{{ item.description }}</p>
              </div>
            </div>
          </template>
        </Tooltip>
      </div>

      <div
        class="relative grid grid-cols-4 gap-x-4 gap-y-3 md:gap-x-6 lg:gap-x-12"
      >
        <FimCheckbox
          id="leftEye"
          :model-value="checkedEyes"
          class="col-span-2 whitespace-nowrap font-medium"
          :label="$t('pdp.lenses.left_eye')"
          item="left"
          text-size="xl"
          @change="toggleCheckbox('left')"
        />
        <FimCheckbox
          id="rightEye"
          :model-value="checkedEyes"
          class="col-span-2 whitespace-nowrap font-medium"
          :label="$t('pdp.lenses.right_eye')"
          item="right"
          text-size="xl"
          @change="toggleCheckbox('right')"
        />

        <template v-for="(entry, key) in pickerOptions" :key="key">
          <Picker
            :id="`picker_left_${key}`"
            v-model="selectedVariantOptions.left[key]"
            :data-testid="`picker_left_${key}`"
            class="col-span-2 mt-1 max-w-[340px]"
            :label="$t(`pdp.lenses.${key}`)"
            :options="entry"
            :semi-disabled="!checkedEyes.includes('left')"
            :placeholder-before-value="0"
            @focus="toggleCheckbox('left')"
            @update:model-value="
              fetchIdForSelections('left', product.referenceKey)
            "
          />
          <Picker
            :id="`picker_right_${key}`"
            v-model="selectedVariantOptions.right[key]"
            :data-testid="`picker_right_${key}`"
            class="col-span-2 mt-1 max-w-[340px] pt-5"
            :label="$t(`pdp.lenses.${key}`)"
            hide-label
            :options="entry"
            :semi-disabled="!checkedEyes.includes('right')"
            :placeholder-before-value="0"
            @focus="toggleCheckbox('right')"
            @update:model-value="
              fetchIdForSelections('right', product.referenceKey)
            "
          />
        </template>
      </div>
    </div>
    <div v-else class="text-base">
      <LazyIconFielmannWarning class="align-center size-8 text-red-400" />
      <span class="font-medium">{{ $t('pdp.lenses.error_headline') }}</span>
      <span class="block">{{ $t('pdp.lenses.error_text') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Product } from '@scayle/storefront-nuxt'
import { useLensesVariants } from '~/composables/pdp/useLensesVariants'
import { useLensSelectionState } from '~/composables/pdp/useLensSelectionState'
import { Tooltip } from 'floating-vue'

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
})

const emit = defineEmits(['input', 'fetching-variant', 'update-error-state'])

const scope = effectScope()
onScopeDispose(() => scope.stop())

const {
  error: hasError,
  informationPopover,
  variantValues: pickerOptions,
  fetchingSelectorOptions,
  fetchIdForSelections,
} = useLensesVariants(`useLensesVariants-${props.product.referenceKey}`)

const { selectedVariantOptions, checkedEyes, quantity, setLensQuantity } =
  useLensSelectionState()

const route = useRoute()

// Make sure to set default quantities in URL if none are set
onMounted(() => {
  if (
    !route.query[QUANTITY_LEFT_KEY_NAME] ||
    !route.query[QUANTITY_RIGHT_KEY_NAME]
  ) {
    setLensQuantity()
  }
})

scope.run(() => {
  if (import.meta.server) {
    return
  }
  watch(hasError, (newValue) => {
    emit('update-error-state', newValue)
  })
})

const toggleCheckbox = (eyePositionIndex: 'left' | 'right') => {
  const newQuantityValue = quantity.value[eyePositionIndex] > 0 ? '0' : '1'
  const params = {
    [QUANTITY_LEFT_KEY_NAME]:
      eyePositionIndex === 'left'
        ? newQuantityValue
        : (route.query[QUANTITY_LEFT_KEY_NAME] as string),
    [QUANTITY_RIGHT_KEY_NAME]:
      eyePositionIndex === 'right'
        ? newQuantityValue
        : (route.query[QUANTITY_RIGHT_KEY_NAME] as string),
  }
  setLensQuantity(params)
}

const isLoading = computed(
  () =>
    fetchingSelectorOptions.value ||
    !selectedVariantOptions?.left ||
    !selectedVariantOptions?.right,
)
</script>
